// Standing dashboard tiles — fired in parallel on first mount. Span values
// are CSS-grid units (12-col grid). Shape MUST stay narrow so the
// useTileStream hook can build/refresh tiles deterministically without
// per-tile branching.
export interface OverviewQuery {
  id: string;
  title: string;
  subtitle?: string;
  span: 'full' | '6' | '4' | '3' | '2';
  query: string;
}

// Six standing questions chosen to exercise the diverse presentation types
// (KPI strip, choropleth, chart, table, composite, smaller chart). Order
// matches the dashboard mockup grid layout.
export const OVERVIEW_QUERIES: OverviewQuery[] = [
  {
    id: 'kpi-strip',
    title: 'Active HRSA programs at a glance',
    subtitle: 'totals across all states + fiscal years',
    span: 'full',
    // Returns multi-stat summary → renders as `stats` envelope (KPI strip)
    query: 'Give me an overview of HRSA program totals — number of HPSAs, MUAs, NHSC providers, Health Center sites, and total grant funding awarded.',
  },
  {
    id: 'mua-by-state',
    title: 'MUAs by state',
    subtitle: 'bubble size = designation count',
    span: '6',
    query: 'Which states have the highest number of Medically Underserved Areas?',
  },
  {
    id: 'hpsa-by-discipline',
    title: 'HPSA designations by discipline',
    span: '3',
    query: 'How many Health Professional Shortage Areas are designated for Primary Care, Mental Health, and Dental?',
  },
  {
    id: 'top-grant-states',
    title: 'Top states by HRSA grants',
    subtitle: 'last fiscal year',
    span: '3',
    query: 'Which states received the most HRSA grant funding in the last fiscal year?',
  },
  {
    id: 'cross-q5',
    title: 'Cross-dataset · counties with HPSA + MUA but no HCP',
    span: '6',
    query: 'Which counties have both a designated HPSA and a Medically Underserved Area but no active Health Center Program Site?',
  },
  {
    id: 'grants-by-program',
    title: 'Grants by program area',
    subtitle: 'last fiscal year',
    span: '6',
    query: 'How is HRSA grant funding distributed across program areas in the last fiscal year?',
  },
];

// Heuristic intent classifier for the persistent ask bar. v1 is keyword-based;
// the LLM-driven version is a follow-up plan.
// TODO: replace heuristic with LLM intent classification
export type Intent = 'modify' | 'append' | 'reset';

const MODIFY_HINTS = ['limit', 'filter', 'show only', 'exclude', 'narrow', 'restrict'];
const RESET_HINTS = ['reset', 'start over', 'new dashboard', 'clear'];

export function classifyIntent(text: string): Intent {
  const t = text.toLowerCase();
  if (RESET_HINTS.some((h) => t.includes(h))) return 'reset';
  if (MODIFY_HINTS.some((h) => t.includes(h))) return 'modify';
  return 'append';
}
